import styled from 'styled-components';
import { TrackedLink } from 'src/components/TrackedLink';
import React, { FC, useCallback, useContext } from 'react';
import { Button } from 'src/components/common/Button';
import { ChevronRight } from 'src/components/icons/ChevronRight';
import { media } from 'src/styles/designs';
import { Close } from 'src/components/icons/Close';
import { LayoutConfigContext } from 'src/providers/LayoutConfigProvider';
import { LayoutRegisterContext } from 'src/providers/LayoutRegisterProvider';
import { BarAnnouncements } from 'src/graphql/schema.graphql';
import { TranslatedItem } from 'src/hooks/directus/useTranslateItem';
import { DirectusImage } from 'src/components/common/DirectusImage';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { lineClamp } from 'src/styles/common';
import { dirRTL } from 'src/styles/theme';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 15px 14px 18px 30px;
  ${media.w.lessThan.desktop} {
    flex-wrap: wrap;
    width: 100%;
    padding: 10px 22px 13px;
  }
  ${dirRTL} {
    padding: 15px 30px 18px 14px;
  }
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  ${media.w.lessThan.desktop} {
    margin-bottom: 6px;
    height: auto;
    width: 100%;
  }
`;

const Logo = styled.div`
  position: relative;
  width: 100%;
  min-width: 55px;
  max-width: 55px;
  height: 33px;
  margin-top: 1px;
  ${media.w.greaterThanOrEqual.desktop} {
    min-width: 73rpx;
    max-width: 73rpx;
    height: calc(100% + 33px);
  }
`;

const Spacer = styled.span`
  display: block;
  opacity: 0.5;
  min-width: 1rpx;
  height: 16px;
  background: ${({ theme }) => theme.text.palette.primary};
  margin: 0 0 0 15px;
  ${media.w.lessThan.desktop} {
    height: 10px;
    margin: 0 15px;
  }
`;

const Text = styled.div`
  width: 100%;
  max-width: 420px;
  font-size: 14px;
  line-height: 16px;
  color: ${({ theme }) => theme.text.palette.primary};
  overflow: hidden;
  ${media.w.greaterThanOrEqual.desktop} {
    margin-left: 15px;
    margin-right: 24px;
    max-height: 100%;
    max-height: 54px;
  }
  p {
    margin: 0;
    ${lineClamp(3)}
  }
`;

const CTAButton = styled(Button)`
  && {
    display: flex;
    min-width: 150px;
    padding-left: 25px;
    padding-right: 25px;
    ${media.w.lessThan.desktop} {
      width: calc(100% - 50px);
      min-height: 32px;
      height: 32px;
      margin-right: 10px;
      ${dirRTL} {
        margin-right: 0;
        margin-left: 10px;
      }
    }
  }
`;

const CloseButton = styled(Button)`
  margin-left: 10px;
  min-width: 32px;
  max-width: 32px;
  height: 32px;
  overflow: hidden;
  ${media.w.lessThan.desktop} {
    margin-left: auto;
    svg {
      width: 19px;
      height: 19px;
    }
  }
  ${dirRTL} {
    margin-left: 0;
    margin-right: 10px;
    ${media.w.lessThan.desktop} {
      margin-right: auto;
    }
  }
`;

export const AnnouncementContent: FC<TranslatedItem<BarAnnouncements>> = ({
  image,
  description,
  ctaLabel,
  ctaLink,
}) => {
  const layoutRegisterContext = useContext(LayoutRegisterContext);
  const layoutConfigContext = useContext(LayoutConfigContext);

  const handleClose = useCallback(() => {
    layoutConfigContext?.hideLayoutComponent('announcement');

    layoutRegisterContext?.deregisterComponent('desktopAnnouncement');
    layoutRegisterContext?.deregisterComponent('mobileAnnouncement');
  }, [layoutRegisterContext, layoutConfigContext]);

  return (
    <Wrapper>
      <Content>
        {image?.id && (
          <>
            <Logo>
              <DirectusImage
                src={image.id}
                layout="fill"
                quality={100}
                objectFit="contain"
              />
            </Logo>
            <Spacer />
          </>
        )}
        <Text>
          <Markdown>{description}</Markdown>
        </Text>
      </Content>
      <TrackedLink href={ctaLink} passHref>
        <CTAButton
          forwardedAs="a"
          icon={ChevronRight}
          iconProps={{ position: 'right' }}
        >
          {ctaLabel}
        </CTAButton>
      </TrackedLink>
      <CloseButton
        kind="text"
        icon={Close}
        iconProps={{ size: 14 }}
        onClick={handleClose}
      />
    </Wrapper>
  );
};
